import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MilestonePlanTaskService, MilestonePlanTask, CreateMilestonePlanTaskDto } from '../../services/milestone-plan-task.service';

@Component({
  selector: 'app-milestone-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './milestone-plan.component.html',
  styleUrl: './milestone-plan.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class MilestonePlanComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  tasks: MilestonePlanTask[] = [];
  selectedTask: MilestonePlanTask | null = null;
  displayTaskDialog: boolean = false;
  isEditTaskMode: boolean = false;
  taskStartDate: Date | null = null;
  taskTargetDate: Date | null = null;
  taskActualDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  taskStatusOptions = [
    { label: 'To Start', value: 'To Start' },
    { label: 'On Track', value: 'On Track' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Off Track', value: 'Off Track' },
    { label: 'Completed', value: 'Completed' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private milestonePlanTaskService: MilestonePlanTaskService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadTasks();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadTasks();
    }
  }

  loadTasks() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.milestonePlanTaskService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tasks) => {
        this.tasks = tasks;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading milestone tasks:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load milestone tasks. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddTaskDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add milestone tasks.'
      });
      return;
    }
    const todayStr = this.formatDateToLocalString(new Date());
    this.selectedTask = {
      id: '',
      projectId: this.projectId,
      taskNumber: this.generateTaskNumber(),
      task: '',
      deliverable: '',
      startDate: todayStr,
      targetDate: '',
      actualDate: '',
      status: 'To Start',
      comments: '',
      displayOrder: this.tasks.length + 1
    };
    this.taskStartDate = new Date();
    this.taskTargetDate = null;
    this.taskActualDate = null;
    this.fieldTouched = {};
    this.isEditTaskMode = false;
    this.displayTaskDialog = true;
  }

  showEditTaskDialog(task: MilestonePlanTask) {
    this.selectedTask = { ...task, status: this.normalizeStatus(task.status) };
    this.taskStartDate = task.startDate ? this.parseLocalDateString(task.startDate) : null;
    this.taskTargetDate = task.targetDate ? this.parseLocalDateString(task.targetDate) : null;
    this.taskActualDate = task.actualDate ? this.parseLocalDateString(task.actualDate) : null;
    this.fieldTouched = {};
    this.isEditTaskMode = true;
    this.displayTaskDialog = true;
  }

  resetTaskForm() {
    this.fieldTouched = {};
    if (!this.selectedTask) return;
    if (this.isEditTaskMode) {
      const task = this.tasks.find(t => t.id === this.selectedTask?.id);
      if (task) {
        this.selectedTask = { ...task };
        this.taskStartDate = task.startDate ? this.parseLocalDateString(task.startDate) : null;
        this.taskTargetDate = task.targetDate ? this.parseLocalDateString(task.targetDate) : null;
        this.taskActualDate = task.actualDate ? this.parseLocalDateString(task.actualDate) : null;
      }
    } else {
      this.selectedTask.taskNumber = this.generateTaskNumber();
      this.selectedTask.task = '';
      this.selectedTask.deliverable = '';
      this.selectedTask.startDate = '';
      this.selectedTask.targetDate = undefined;
      this.selectedTask.actualDate = undefined;
      this.selectedTask.status = 'To Start';
      this.selectedTask.comments = '';
      this.taskStartDate = null;
      this.taskTargetDate = null;
      this.taskActualDate = null;
    }
  }

  saveTask() {
    this.fieldTouched['task'] = true;

    if (!this.selectedTask || !this.selectedTask.task.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.taskStartDate) {
      this.selectedTask.startDate = this.formatDateToLocalString(this.taskStartDate);
    }

    if (this.taskTargetDate) {
      this.selectedTask.targetDate = this.formatDateToLocalString(this.taskTargetDate);
    } else {
      this.selectedTask.targetDate = undefined;
    }

    if (this.taskActualDate) {
      this.selectedTask.actualDate = this.formatDateToLocalString(this.taskActualDate);
    } else {
      this.selectedTask.actualDate = undefined;
    }

    if (this.isEditTaskMode) {
      // Update existing task
      this.milestonePlanTaskService.updateTask(this.selectedTask.id, this.selectedTask).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedTask) => {
          const index = this.tasks.findIndex(t => t.id === updatedTask.id);
          if (index !== -1) {
            this.tasks[index] = updatedTask;
            // Re-sort by display order
            this.tasks.sort((a, b) => a.displayOrder - b.displayOrder);
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Milestone task updated successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error updating milestone task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update milestone task. Please try again.'
          });
        }
      });
    } else {
      // Create new task
      const createDto: CreateMilestonePlanTaskDto = {
        projectId: this.projectId,
        taskNumber: this.selectedTask.taskNumber,
        task: this.selectedTask.task,
        deliverable: this.selectedTask.deliverable,
        startDate: this.selectedTask.startDate,
        targetDate: this.selectedTask.targetDate,
        actualDate: this.selectedTask.actualDate,
        status: this.selectedTask.status,
        comments: this.selectedTask.comments,
        displayOrder: this.selectedTask.displayOrder
      };

      this.milestonePlanTaskService.addTask(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newTask) => {
          this.tasks.push(newTask);
          this.tasks.sort((a, b) => a.displayOrder - b.displayOrder);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Milestone task added successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error adding milestone task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add milestone task. Please try again.'
          });
        }
      });
    }
  }

  deleteTask(task: MilestonePlanTask) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${task.task || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.milestonePlanTaskService.deleteTask(task.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.tasks.findIndex(t => t.id === task.id);
            if (index !== -1) {
              this.tasks.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Milestone task deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting milestone task:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete milestone task. Please try again.'
            });
          }
        });
      }
    });
  }

  moveTaskUp(task: MilestonePlanTask) {
    const currentIndex = this.tasks.findIndex(t => t.id === task.id);
    if (currentIndex > 0) {
      // Swap with the task above
      const taskAbove = this.tasks[currentIndex - 1];
      this.reorderTask(task.id, taskAbove.displayOrder);
    }
  }

  moveTaskDown(task: MilestonePlanTask) {
    const currentIndex = this.tasks.findIndex(t => t.id === task.id);
    if (currentIndex < this.tasks.length - 1) {
      // Swap with the task below
      const taskBelow = this.tasks[currentIndex + 1];
      this.reorderTask(task.id, taskBelow.displayOrder);
    }
  }

  reorderTask(id: string, newDisplayOrder: number) {
    this.milestonePlanTaskService.reorderTask(id, newDisplayOrder).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loadTasks(); // Reload to get updated order
      },
      error: (error) => {
        console.error('Error reordering task:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reorder task. Please try again.'
        });
      }
    });
  }

  generateTaskNumber(): string {
    if (this.tasks.length === 0) {
      return '00001';
    }
    const maxId = Math.max(...this.tasks.map(t => {
      const num = parseInt(t.taskNumber.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxId + 1).padStart(5, '0');
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone off-by-one). */
  private parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format Date to YYYY-MM-DD using local date. */
  private formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatTaskDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Coerce a stored status onto one of `taskStatusOptions`, so the edit dialog never opens with a
   * blank dropdown. Covers legacy casing ("Off track") and values written by non-UI callers before
   * the API validated them ("Not Started"); anything unrecognized falls back to "To Start".
   */
  private normalizeStatus(status: string): string {
    if (this.taskStatusOptions.some(o => o.value === status)) {
      return status;
    }
    const match = this.taskStatusOptions.find(
      o => o.value.toLowerCase() === (status || '').trim().toLowerCase()
    );
    return match ? match.value : 'To Start';
  }

  /** Normalize status for display (e.g. legacy "Off track" -> "Off Track"). */
  getDisplayStatus(status: string): string {
    return status === 'Off track' ? 'Off Track' : (status || '');
  }

  getTaskStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'To Start':
        return 'secondary';
      case 'On Track':
      case 'Completed':
        return 'success';
      case 'On Hold':
        return 'warn';
      case 'Off Track':
      case 'Off track': // Backend may return legacy value until migration runs
        return 'danger';
      default:
        return 'secondary';
    }
  }
}

